import { Menu, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { type ReactElement, use, useCallback } from "react"

import { SelectItemTemplate } from "../../components/MenuGroups/constants"
import { FilterRendererContext } from "../contexts/FilterRendererContext/context"
import type { AnyFilterDef, FilterModifiersOfFilterDef } from "../defs/BaseFilterDef"
import { Filter } from "../filter"

interface FilterModifierDropdownProps<TFilterDef extends AnyFilterDef> extends MenuVariantProps, MenuRootProps {
	children: ReactElement
	filterDef: TFilterDef
	filter: Filter<TFilterDef>
	onModifierChange?: (modifierId: FilterModifiersOfFilterDef<TFilterDef>) => void
}

export const FilterModifierDropdown = <TFilterDef extends AnyFilterDef>(
	props: FilterModifierDropdownProps<TFilterDef>,
) => {
	const filterRendererContext = use(FilterRendererContext)
	const {
		children,
		filter,
		filterDef,
		size = filterRendererContext.size,
		variant,
		accentColor,
		radius,
		onModifierChange,
	} = props

	const handleValueChange = useCallback(
		(modifier: FilterModifiersOfFilterDef<TFilterDef>) => {
			filter.setFilterModifier(modifier)
			onModifierChange?.(modifier)
		},
		[filter, onModifierChange],
	)

	return (
		<Menu.Root align="start">
			<Menu.ClickTrigger>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} variant={variant} radius={radius} accentColor={accentColor}>
				<Menu.SelectGroup required value={filter.getFilterModifier()} onValueChange={handleValueChange}>
					{filterDef.getFilterModifiers().map(([id, modifier]) => {
						return (
							<SelectItemTemplate value={id} key={id}>
								{modifier.modifier.render(filter.getFilterValue())}
							</SelectItemTemplate>
						)
					})}
				</Menu.SelectGroup>
			</Menu.Content>
		</Menu.Root>
	)
}
